import React, { useCallback } from "react";
import { Box, CloseButton, Flex, ToastProps, useToast as useChakraToast } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { colours } from "@/theme/colours";
import { Text } from "@chakra-ui/react";
import { ChakraFAIcon } from "./Icon";

const StyledToast = styled(Box)`
	display: flex;
	align-content: start;
	position: relative;
	padding: 12px;
	width: 400px;
	color: ${colours.text.paragraph.regular};
	background-color: ${colours.accent.green["100"]};
	border: 1px solid ${colours.accent.green["300"]};
	border-radius: 5px;
`;

const SuccessToast = styled(StyledToast)`
	background-color: ${colours.accent.green["100"]};
	border: 1px solid ${colours.accent.green["300"]};
`;

const InfoToast = styled(StyledToast)`
	background-color: ${colours.accent.turquoise["100"]};
	border: 1px solid ${colours.accent.turquoise["300"]};
`;

const WarningToast = styled(StyledToast)`
	background-color: ${colours.accent.orange["100"]};
	border: 1px solid ${colours.accent.orange["300"]};
`;

const ErrorToast = styled(StyledToast)`
	background-color: ${colours.accent.red["100"]};
	border: 1px solid ${colours.accent.red["300"]};
`;

export const ToastBody = ({ title, description, isClosable, onClose }: ToastProps): JSX.Element => (
	<Flex alignItems="center" justifyContent="space-between" width="100%">
		<Flex flexDirection={"column"}>
			<Text fontFamily="Roboto" fontWeight="500">
				{title}
			</Text>
			{description && (
				<Text fontFamily="Roboto" fontWeight="400">
					{description}
				</Text>
			)}
		</Flex>

		{isClosable && <CloseButton size="sm" onClick={onClose} data-testid="closeAll-toast" />}
	</Flex>
);

/**
 * This hook wraps the native Chakra useToast hook to use a custom toast body and provide a consistent API for toast notifications.
 */
export const useClasswizeToast = (): {
	closeAll: () => void;
	successToast: (props: ToastProps) => void;
	infoToast: (props: ToastProps) => void;
	warningToast: (props: ToastProps) => void;
	errorToast: (props: ToastProps) => void;
} => {
	const chakraToast = useChakraToast();

	const closeAll = (): void => {
		chakraToast.closeAll();
	};

	// Wrapped in useCallback to prevent unnecessary re-renders when used as a dependency in useEffect
	const successToast = useCallback(
		({ title, description, isClosable, duration }: ToastProps) =>
			chakraToast({
				duration: duration || 9000,
				render: () => (
					<SuccessToast>
						<ChakraFAIcon icon="fa-circle-check" color={colours.accent.green[400]} boxSize="16px" mr="12px" mt="3px" />
						<ToastBody title={title} description={description} isClosable={isClosable} onClose={closeAll} />
					</SuccessToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const infoToast = useCallback(
		({ title, description, isClosable, duration }: ToastProps) =>
			chakraToast({
				duration: duration || 9000,
				render: () => (
					<InfoToast>
						<ChakraFAIcon variant="solid" icon="fa-circle-info" color={colours.accent.turquoise[400]} boxSize="16px" mr="12px" mt="3px" />
						<ToastBody title={title} description={description} isClosable={isClosable} onClose={closeAll} />
					</InfoToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const warningToast = useCallback(
		({ title, description, isClosable, duration }: ToastProps) =>
			chakraToast({
				duration: duration || 9000,
				render: () => (
					<WarningToast>
						<ChakraFAIcon
							variant="solid"
							icon="fa-triangle-exclamation"
							color={colours.accent.orange[400]}
							boxSize="16px"
							mr="12px"
							mt="3px"
						/>
						<ToastBody title={title} description={description} isClosable={isClosable} onClose={closeAll} />
					</WarningToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const errorToast = useCallback(
		({ title, description, isClosable, duration }: ToastProps) =>
			chakraToast({
				duration: duration || 9000,
				render: () => (
					<ErrorToast>
						<ChakraFAIcon variant="solid" icon="fa-circle-exclamation" color={colours.accent.red[400]} boxSize="16px" mr="12px" mt="3px" />
						<ToastBody title={title} description={description} isClosable={isClosable} onClose={closeAll} />
					</ErrorToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return { closeAll, successToast, infoToast, warningToast, errorToast };
};

export const useToast = useClasswizeToast;
